/**
 * Page de liste des inscriptions avec filtres.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { inscriptionService, getMediaUrl } from '../services/api';
import { notify } from '../components/NotificationContainer';
import logger from '../utils/logger';

const InscriptionsList = () => {
  const navigate = useNavigate();
  const [inscriptions, setInscriptions] = useState([]);
  const [inscriptionsArchivees, setInscriptionsArchivees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('ACTIVES'); // Par défaut, afficher les actives

  useEffect(() => {
    loadInscriptions();
    loadInscriptionsArchivees();
  }, []);

  const loadInscriptions = async () => {
    try {
      const response = await inscriptionService.getAll();
      setInscriptions(response.data.results || response.data);
    } catch (error) {
      logger.error('Erreur lors du chargement des inscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInscriptionsArchivees = async () => {
    try {
      const response = await inscriptionService.getMesInscriptionsArchivees();
      setInscriptionsArchivees(response.data.inscriptions_archivees || []);
    } catch (error) {
      logger.error('Erreur lors du chargement des inscriptions archivées:', error);
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'CONFIRMEE':
        return 'Confirmée';
      case 'EN_ATTENTE':
        return 'En attente';
      case 'EN_ATTENTE_VALIDATION_PARTENAIRE':
        return 'En attente validation';
      case 'TERMINE':
        return 'Terminée';
      case 'ANNULEE':
        return 'Annulée';
      default:
        return statut;
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'CONFIRMEE':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'EN_ATTENTE':
        return 'bg-sky-50 text-sky-700 border border-sky-200';
      case 'EN_ATTENTE_VALIDATION_PARTENAIRE':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'TERMINE':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'ANNULEE':
        return 'bg-slate-50 text-slate-600 border border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border border-slate-200';
    }
  };

  const getActionLabel = (inscription) => {
    return 'Gérer';
  };

  // Filtrer les inscriptions selon le statut sélectionné
  const inscriptionsFiltrees = inscriptions.filter((inscription) => {
    if (filtreStatut === 'TOUTES') return true;
    if (filtreStatut === 'ACTIVES') return inscription.statut !== 'ANNULEE';
    return inscription.statut === filtreStatut;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Inscriptions</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              {inscriptionsFiltrees.length} inscription{inscriptionsFiltrees.length > 1 ? 's' : ''}
            </p>
          </div>

          {/* Filtre discret */}
          <select
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
            className="text-sm border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white w-full sm:w-auto"
          >
            <option value="ACTIVES">Actives</option>
            <option value="CONFIRMEE">Confirmées</option>
            <option value="EN_ATTENTE">En attente</option>
            <option value="ANNULEE">Annulées</option>
            <option value="TOUTES">Toutes</option>
          </select>
        </div>

        {/* Liste des inscriptions */}
        {inscriptionsFiltrees.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 rounded-full flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4">
              📋
            </div>
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Aucune inscription</h2>
            <p className="text-xs sm:text-sm text-gray-500 mb-6">
              {filtreStatut === 'ACTIVES'
                ? "Vous n'avez pas d'inscription active"
                : `Aucune inscription ${getStatutLabel(filtreStatut).toLowerCase()}`}
            </p>
            <button
              onClick={() => navigate('/sejours')}
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
            >
              Découvrir les séjours
            </button>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
          {inscriptionsFiltrees.map((inscription) => (
            <div
              key={inscription.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 hover:shadow transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                {/* Photo de l'enfant */}
                <div className="flex-shrink-0 self-start sm:self-auto">
                  {(inscription.enfant_details?.photo_url || inscription.enfant_details?.photo) ? (
                    <img
                      src={inscription.enfant_details.photo_url || getMediaUrl(inscription.enfant_details.photo)}
                      alt={`${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-100 border-2 border-gray-200 rounded-full flex items-center justify-center text-xl sm:text-2xl">
                      {inscription.enfant_details?.age < 6 ? '👶' : inscription.enfant_details?.age < 12 ? '🧒' : '👦'}
                    </div>
                  )}
                </div>

                {/* Informations */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                      {inscription.sejour_details?.titre}
                    </h3>
                    {/* Badge statut sur mobile */}
                    <span
                      className={`sm:hidden inline-flex self-start px-2.5 py-1 text-xs font-medium rounded-full ${getStatutColor(
                        inscription.statut
                      )}`}
                    >
                      {getStatutLabel(inscription.statut)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                    <span className="text-gray-700 font-medium">
                      {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
                    </span>

                    <span className="text-gray-400">•</span>

                    <span className="text-gray-600">
                      {inscription.enfant_details?.age} ans
                    </span>

                    {inscription.tranche_prix_details && (
                      <>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600 text-xs">
                          Tranche {inscription.tranche_prix_details.age_min}-{inscription.tranche_prix_details.age_max} ans
                        </span>
                      </>
                    )}

                    <span className="hidden sm:inline text-gray-400">•</span>

                    <span className="text-gray-600 hidden sm:inline">
                      {new Date(inscription.sejour_details?.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(inscription.sejour_details?.date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>

                {/* Badge statut - desktop uniquement */}
                <span
                  className={`hidden sm:inline-flex px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatutColor(
                    inscription.statut
                  )}`}
                >
                  {getStatutLabel(inscription.statut)}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigate(`/inscriptions/${inscription.id}`)}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white"
                  >
                    {getActionLabel(inscription)}
                  </button>

                  {inscription.statut !== 'ANNULEE' && (
                    <button
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir annuler cette inscription ?')) {
                          inscriptionService
                            .cancel(inscription.id)
                            .then(() => {
                              loadInscriptions();
                            })
                            .catch(() => {
                              notify.error('Erreur lors de l\'annulation');
                            });
                        }
                      }}
                      className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-colors whitespace-nowrap"
                    >
                      Annuler
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Section des inscriptions archivées */}
      {inscriptionsArchivees.length > 0 && (
        <div className="mt-8">
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-700">Inscriptions archivées</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {inscriptionsArchivees.length}
            </span>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {inscriptionsArchivees.map((inscription) => (
              <div
                key={inscription.id}
                className="bg-gray-50 rounded-lg shadow-sm border border-gray-300 p-3 sm:p-4"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  {/* Icône archivé */}
                  <div className="flex-shrink-0 self-start sm:self-auto">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-200 border-2 border-gray-300 rounded-full flex items-center justify-center text-xl sm:text-2xl">
                      📦
                    </div>
                  </div>

                  {/* Informations minimales */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-700 break-words">
                        {inscription.sejour_titre}
                      </h3>
                      <span className="inline-flex self-start px-2.5 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                        Archivé
                      </span>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                      <span className="text-gray-700 font-medium">
                        {inscription.enfant_prenom} {inscription.enfant_nom}
                      </span>

                      <span className="text-gray-400">•</span>

                      <span className="text-gray-600">
                        {new Date(inscription.sejour_date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(inscription.sejour_date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>

                      <span className="text-gray-400">•</span>

                      <span className="text-gray-600">
                        Payé: {inscription.montant_paye}€ / {inscription.montant_total}€
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 mt-2">
                      Archivé le {new Date(inscription.date_archivage).toLocaleDateString('fr-FR')} - Données personnelles supprimées (RGPD)
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default InscriptionsList;
